import Footer from "./components/layouts/Footer";
import Header from "./components/layouts/Header";
import {
  Routes,
  Route,
  BrowserRouter,
  Navigate,
  Outlet,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ToastContainer } from "react-toastify";
import Home from "./components/pages/Home";
import Login from "./components/pages/Login";
import AdminDashboard from "./components/dashboard/admin/AdminDashboard";
import AdminMessages from "./components/dashboard/admin/AdminMessages";
import AdminMinistries from "./components/dashboard/admin/AdminMinistries";
import AdminUsers from "./components/dashboard/admin/AdminUsers";
import AdminCinepro from "./components/dashboard/admin/AdminCinepro";
import AdminResources from "./components/dashboard/admin/AdminResources";
import AdminProfessors from "./components/dashboard/admin/AdminProfessors";
import AdminForum from "./components/dashboard/admin/AdminForum";
import AdminLive from "./components/dashboard/admin/AdminLive";
import AdminEvaluation from "./components/dashboard/admin/AdminEvaluation";
import AdminCertificate from "./components/dashboard/admin/AdminCertificate";
import AdminProfile from "./components/dashboard/admin/AdminProfile";
import EtudiantDashboard from "./components/dashboard/etudiant/EtudiantDashboard";

import ProfesseurDashboard from "./components/dashboard/professeur/ProfDashboard";
import ParrainDashboard from "./components/dashboard/parrain/ParrainDashboard";
import ProtectedRoute from "./components/ProtectedRoutes";
import AdminDashboardLayout from "./components/dashboard/layouts/AdminDashboardLayout";
import EtudiantDashboardLayout from "./components/dashboard/layouts/EtudiantDashboardLayout";
import ProfesseurDashboardLayout from "./components/dashboard/layouts/ProfesseurDashboardLayout";
import ParrainDashboardLayout from "./components/dashboard/layouts/ParrainDashboardLayout";
import ForgotPassword from "./components/pages/Forgot-password";
import VerifyEmail from "./components/pages/Verify-email";
import ResetPassword from "./components/pages/ResetPassword";
import RessourcesPedagogiques from "./components/dashboard/etudiant/RessourcesPedagogiques";
import ProfesseursExpertsDashboard from "./components/dashboard/etudiant/ProfesseursExperts";
import CinePro from "./components/dashboard/etudiant/CinePro";
import EtudiantProfile from "./components/dashboard/etudiant/EtudiantProfile";
import EtudiantForum from "./components/dashboard/etudiant/EtudiantForum";
import ForumTopicDetail from "./components/dashboard/etudiant/ForumTopicDetail";
import LiveDirect from "./components/dashboard/etudiant/LiveDirect";
import EtudiantCertificat from "./components/dashboard/etudiant/EtudiantCertificat";
import EtudiantEvaluation from "./components/dashboard/etudiant/EtudiantEvaluation";
import GuideIntelligence from "./components/dashboard/etudiant/GuideIntelligence";
import AdminChat from "./components/dashboard/admin/AdminChat";
import EtudiantChat from "./components/dashboard/etudiant/EtudiantChat";
import LiveDetail from "./components/dashboard/etudiant/LiveDetail";
import ProfesseurForumTopicDetail from "./components/dashboard/professeur/ProfesseurForumTopicDetail";
import ProfesseurForum from "./components/dashboard/professeur/ProfesseurForum";
import FilleulDetail from "./components/dashboard/parrain/FilleulDetail";
import ProfesseurExpert from "./components/dashboard/professeur/ProfesseurExpert";
import ResourcesPedagogiques from "./components/dashboard/professeur/ResourcesPedagogiques";
import ProfesseurChat from "./components/dashboard/professeur/ProfesseurChat";
import ProfesseurLive from "./components/dashboard/professeur/ProfesseurLive";

// Créer une instance de QueryClient
const queryClient = new QueryClient();

// Composant de mise en page qui affiche le Header et Footer
function MainLayout() {
  return (
    <>
      <Header />
      <main>
        <Outlet />
      </main>
      <Footer />
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ToastContainer position="top-right" autoClose={3000} />
        <Routes>
          {/* Routes d'authentification */}
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Routes protégées du dashboard */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <AdminDashboardLayout>
                  <Routes>
                    <Route path="dashboard" element={<AdminDashboard />} />
                    <Route path="messages" element={<AdminMessages />} />
                    <Route path="ministries" element={<AdminMinistries />} />
                    <Route path="users" element={<AdminUsers />} />
                    <Route path="cinépro" element={<AdminCinepro />} />
                    <Route
                      path="ressources-pedagogiques"
                      element={<AdminResources />}
                    />
                    <Route
                      path="professeurs-experts"
                      element={<AdminProfessors />}
                    />
                    <Route path="forum" element={<AdminForum />} />
                    <Route path="chat" element={<AdminChat />} />
                    <Route path="live-direct" element={<AdminLive />} />
                    <Route path="evaluation" element={<AdminEvaluation />} />
                    <Route path="certificat" element={<AdminCertificate />} />
                    <Route path="profile" element={<AdminProfile />} />
                  </Routes>
                </AdminDashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Routes protégées étudiant */}
          <Route
            path="/etudiant/*"
            element={
              <ProtectedRoute requiredRole="APPRENANT">
                <EtudiantDashboardLayout>
                  <Routes>
                    <Route path="dashboard" element={<EtudiantDashboard />} />
                    <Route path="cinepro" element={<CinePro />} />
                    <Route path="cinepro/:videoId" element={<CinePro />} />
                    <Route path="cinepro/video/:title" element={<CinePro />} />
                    <Route
                      path="ressources"
                      element={<RessourcesPedagogiques />}
                    />
                    <Route
                      path="professeurs-experts"
                      element={<ProfesseursExpertsDashboard />}
                    />
                    <Route path="forum" element={<EtudiantForum />} />
                    <Route
                      path="forum/sujet/:topicId"
                      element={<ForumTopicDetail />}
                    />
                    <Route path="live" element={<LiveDirect />} />
                    <Route path="live/:meetingId" element={<LiveDetail />} />
                    <Route path="chat" element={<EtudiantChat />} />
                    <Route path="certificat" element={<EtudiantCertificat />} />
                    <Route path="evaluation" element={<EtudiantEvaluation />} />
                    <Route
                      path="guide-intelligence"
                      element={<GuideIntelligence />}
                    />
                    <Route path="profil" element={<EtudiantProfile />} />
                  </Routes>
                </EtudiantDashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Routes protégées professeur */}
          <Route
            path="/professeur/*"
            element={
              <ProtectedRoute requiredRole="FORMATEUR">
                <ProfesseurDashboardLayout>
                  <Routes>
                    <Route path="dashboard" element={<ProfesseurDashboard />} />
                    <Route
                      path="ressources"
                      element={<ResourcesPedagogiques />}
                    />
                    <Route
                      path="professeurs-experts"
                      element={<ProfesseurExpert />}
                    />
                    <Route path="forum" element={<ProfesseurForum />} />
                    <Route
                      path="forum/sujet/:topicId"
                      element={<ProfesseurForumTopicDetail />}
                    />
                    <Route path="chat" element={<ProfesseurChat />} />
                    <Route path="live" element={<ProfesseurLive />} />
                  </Routes>
                </ProfesseurDashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Routes protégées parrain */}
          <Route
            path="/parrain/*"
            element={
              <ProtectedRoute requiredRole="SUPERVISEUR">
                <ParrainDashboardLayout>
                  <Routes>
                    <Route path="dashboard" element={<ParrainDashboard />} />
                    <Route
                      path="filleul/:filleulId"
                      element={<FilleulDetail />}
                    />
                  </Routes>
                </ParrainDashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Routes publiques */}
          <Route path="/" element={<MainLayout />}>
            <Route index element={<Home />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
